import classNames from 'classnames'
import { useTheme } from '@homebot/shared-ui-swashbuckle'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Text, Tooltip } from '../../../..'
import { FormattedMessage } from 'react-intl'
import {
  NUMBER_OF_CLUSTER_TOOL_TIP_VIEWS,
  INITIAL_DELAY_UNTIL_CLUSTER_TOOLTIP,
  SMALL_CLUSTER_SIZE_CUTOFF,
  MEDIUM_CLUSTER_SIZE_CUTOFF,
  MAX_NUMBER_OF_TOOLTIP_VIEWS,
  LARGE_CLUSTER_SIZE_CUTOFF,
} from '../../../../../constants/listings'
import styles from './Cluster.module.scss'
import { inRange } from 'lodash'
import { FadeOnMount } from '../../../../shared/Animations'

export interface ClusterProps {
  id: string
  numberOfPointsInCluster: number
  onClick: (id: string, e: React.MouseEvent<HTMLButtonElement>) => void
}

const Cluster = ({ id, numberOfPointsInCluster, onClick }: ClusterProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const timeoutId = useRef<NodeJS.Timeout | null>(null)

  const fetchNumberOfClusterToolTipViews = () => {
    const views = localStorage.getItem(NUMBER_OF_CLUSTER_TOOL_TIP_VIEWS)

    return views ? JSON.parse(views) : 0
  }

  const numberOfToolTipViews: number = fetchNumberOfClusterToolTipViews()

  useEffect(() => {
    if (numberOfToolTipViews && numberOfToolTipViews >= 0) return
    localStorage.setItem(NUMBER_OF_CLUSTER_TOOL_TIP_VIEWS, JSON.stringify(0))
  }, [numberOfToolTipViews])

  const { theme } = useTheme()
  const isLightThemeType = theme.isLightThemeType
  const isDarkThemeType = !isLightThemeType

  const sizingStyles = size => {
    return {
      clusterSize: styles[`${size}Cluster`],
      tooltipSize: styles[`${size}Tooltip`],
    }
  }

  const sizeClassName = useMemo(() => {
    const num = numberOfPointsInCluster
    if (inRange(num, SMALL_CLUSTER_SIZE_CUTOFF)) {
      return sizingStyles('small')
    }

    if (inRange(num, SMALL_CLUSTER_SIZE_CUTOFF, MEDIUM_CLUSTER_SIZE_CUTOFF)) {
      return sizingStyles('medium')
    }

    if (inRange(num, MEDIUM_CLUSTER_SIZE_CUTOFF, LARGE_CLUSTER_SIZE_CUTOFF)) {
      return sizingStyles('large')
    }

    return sizingStyles('xLarge')
  }, [numberOfPointsInCluster])

  const clusterClassName = classNames(styles.base, sizeClassName?.clusterSize, {
    [styles.dark]: isDarkThemeType,
    [styles.light]: isLightThemeType,
  })

  const setHover = bool => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }

    if (bool) {
      if (numberOfToolTipViews >= MAX_NUMBER_OF_TOOLTIP_VIEWS) return
      timeoutId.current = setTimeout(() => {
        const newNumberOfToolTipViews = numberOfToolTipViews + 1
        localStorage.setItem(
          NUMBER_OF_CLUSTER_TOOL_TIP_VIEWS,
          JSON.stringify(newNumberOfToolTipViews)
        )

        setIsHovering(bool)
      }, INITIAL_DELAY_UNTIL_CLUSTER_TOOLTIP)
    } else {
      setIsHovering(bool)
    }
  }

  return (
    <>
      <button
        data-cluster-id={id}
        className={clusterClassName}
        onClick={e => onClick(id, e)}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
      >
        <Text element='span' variant='white' size='l' weight='bold'>
          {numberOfPointsInCluster}
        </Text>
      </button>
      {isHovering && (
        <FadeOnMount
          extraStyles={{
            zIndex: 80,
            position: 'relative',
            transform: 'translateY(8px)',
          }}
          buffer={0}
          duration={90}
        >
          <Tooltip
            className={classNames(styles.tooltip, sizeClassName?.tooltipSize)}
          >
            <Text
              element='span'
              weight='bold'
              variant={isLightThemeType ? 'neutralWarm' : 'white'}
            >
              <FormattedMessage
                id='Cluster.tooltip.cta'
                defaultMessage='Click to zoom'
              />
            </Text>
          </Tooltip>
        </FadeOnMount>
      )}
    </>
  )
}

export default Cluster
